import { defineStore } from 'pinia'
import sortBy from 'lodash/sortBy'
import type {
  ScheduleEventQuarter,
  BoxScore,
  GameStatistics,
  ScheduleEventGameData,
  WebsocketMessage,
} from '~/types'
import { liveGameDataGameInfoDto } from '~/dto/liveGameDataGameInfoDto'
import { WebsocketFeedType } from '~/enums/websocket-feed-type'

export const useLiveGamesStore = defineStore('live-games', () => {
  const logger = useLogger('live-games')

  const streams = ref<Map<string, ScheduleEventGameData.Model>>(new Map())
  const boxScores = ref<Map<string, BoxScore.Model>>(new Map())
  const gamesStatistics = ref<Map<string, GameStatistics.Model>>(new Map())
  const playsByPeriodsData = ref<Map<string, ScheduleEventQuarter.Model[]>>(new Map())
  const boxScoresReceivedMap = ref<Map<string, number>>(new Map())

  const addBoxScoreFromMessage = (message: WebsocketMessage.BoxScoreMessage): void => {
    logger.debug('Process BoxScore message', message)

    const {
      receivedAt,
      message: boxScore,
    } = message

    if (boxScoresReceivedMap.value.has(boxScore.id)) {
      const lastReceivedAt = boxScoresReceivedMap.value.get(boxScore.id)

      logger.debug(`BoxScore ${boxScore.id} message received at ${receivedAt}`)

      if (lastReceivedAt && lastReceivedAt > receivedAt) {
        logger.info(`Skipping old boxScore: ${boxScore.id}`, {
          current: lastReceivedAt,
          new: receivedAt,
        })

        return
      }
    }

    boxScoresReceivedMap.value.set(boxScore.id, receivedAt)

    addBoxScore(boxScore)
  }

  const addBoxScore = (data: MaybeRef<BoxScore.Model>): void => {
    logger.debug('Process BoxScore', data)

    const boxScore = unref(data)

    boxScores.value = new Map(boxScores.value).set(boxScore.id, boxScore)
  }

  const addPlaysByPeriods = (srIdRef: MaybeRef<string | undefined | null>, data: ScheduleEventQuarter.Model[]): void => {
    const srId = unref(srIdRef)

    if (!srId) {
      return
    }

    logger.debug('Process PlaysByPeriods', srId, data)

    playsByPeriodsData.value = new Map(playsByPeriodsData.value).set(srId, sortBy(data, 'sequence'))
  }

  const addGameStatistics = (data: MaybeRef<GameStatistics.Model>): void => {
    const gameStatistics = unref(data)

    gamesStatistics.value = new Map(gamesStatistics.value).set(gameStatistics.id, gameStatistics)
  }

  const processMessage = (message: WebsocketMessage.EventMessage): void => {
    logger.debug('Process Message', message)

    const {
      sportradarGameId,
      uuid,
      receivedAt,
    } = message

    if (!message?.message?.payload?.game) {
      logger.warn(`Skipping message without game: ${uuid}`, message)

      return
    }

    if (!streams.value.has(sportradarGameId)) {
      streams.value.set(sportradarGameId, {
        receivedAt,
        game: liveGameDataGameInfoDto(message),
      })
    }

    const gameData = streams.value.get(sportradarGameId)!

    if (gameData.receivedAt > receivedAt) {
      logger.info(`Skipping old message: ${uuid}`, {
        current: gameData.receivedAt,
        new: receivedAt,
        message,
      })

      return
    }

    gameData.receivedAt = receivedAt
    gameData.game = liveGameDataGameInfoDto(message)

    streams.value = new Map(streams.value).set(sportradarGameId, unref(gameData))

    // TODO append pbp to the stats?
  }

  const processMessages = (messages: WebsocketMessage.Model[]): void => {
    for (const message of messages) {
      switch (message.feedType) {
        case WebsocketFeedType.EVENTS:
          processMessage(message as WebsocketMessage.EventMessage)
          break
        case WebsocketFeedType.STATISTICS:
          updateStatisticsFromMessage(message as WebsocketMessage.StatisticsMessage)
          break
        case WebsocketFeedType.BOXSCORE:
          addBoxScoreFromMessage(message as WebsocketMessage.BoxScoreMessage)
          break
      }
    }
  }

  const updateStatisticsFromMessage = (message: WebsocketMessage.StatisticsMessage): void => {
    // TODO can we use it?
    logger.debug('statistics', message)
  }

  const clearData = (): void => {
    streams.value.clear()
    boxScores.value.clear()
    gamesStatistics.value.clear()
    playsByPeriodsData.value.clear()
    boxScoresReceivedMap.value.clear()
  }

  const getStreamById = (gameIdRef: MaybeRef<string | undefined | null>): Maybe<ScheduleEventGameData.Model> => {
    const gameId = unref(gameIdRef)

    if (!gameId) {
      return null
    }

    const gameData: Maybe<ScheduleEventGameData.Model> = streams.value.get(gameId) || {
      receivedAt: 0,
      game: null,
    }

    const boxScore: Maybe<BoxScore.Model> = boxScores.value.get(gameId) ?? null
    const gameStatistics: Maybe<GameStatistics.Model> = gamesStatistics.value.get(gameId) ?? null
    const playsByPeriods: Maybe<ScheduleEventQuarter.Model[]> = playsByPeriodsData.value.get(gameId) ?? []

    return {
      ...gameData,
      boxScore,
      gameStatistics,
      playsByPeriods,
    }
  }

  const removeStream = (gameId: MaybeRef<string>): void => {
    streams.value.delete(unref(gameId))
  }

  const removeBoxScore = (gameId: MaybeRef<string>): void => {
    boxScores.value.delete(unref(gameId))
    boxScoresReceivedMap.value.delete(unref(gameId))
  }

  return {
    streams,
    boxScores,
    gamesStatistics,
    playsByPeriodsData,

    removeBoxScore,
    addBoxScore,
    addBoxScoreFromMessage,
    addGameStatistics,
    processMessage,
    processMessages,
    updateStatisticsFromMessage,
    clearData,
    getStreamById,
    removeStream,
    addPlaysByPeriods,
  }
})
